/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import Peripherals from '@appblocks/codegeneration/Peripherals';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: CodeBlock[] = [];

    let PORT = feature.getSettingValue('PORT');
    if (PORT.indexOf('/') === 0) {
        PORT = `{${PORT}}`;
    }
    const NAME = feature.getSettingValue('NAME');
    const sda = feature.getSettingValue('SDA');
    const scl = feature.getSettingValue('SCL');
    const PULL = feature.getSettingValue('PULL');
    if (!PORT || !sda || !scl) {
        return blocks;
    }

    let pinconfigCode = '';
    let pullCode = '';
    if (PULL === 'up') {
        pullCode = 'bias-pull-up;';
    } else if (PULL === 'down') {
        pullCode = 'bias-pull-down;';
    }

    const device = PlatformUtils.getDeviceDefinition(feature.project.device.id);
    if (device.soc.indexOf('esp') === 0) {
        pinconfigCode = `pinmux = <${PORT.toUpperCase()}_SDA_GPIO${sda}>, 
        <${PORT.toUpperCase()}_SCL_GPIO${scl}>;`;
    } else if (device.soc.indexOf('nrf') === 0) {
        const sdaPeripheral = PlatformUtils.getPeripheral(feature.project.device.id, sda, 'gpio');
        const sclPeripheral = PlatformUtils.getPeripheral(feature.project.device.id, scl, 'gpio');
        let peripheralDriver = 'TWIM';
        for (let i = 0; i < device.peripherals.length; i++) {
            if (device.peripherals[i].id === PORT) {
                const compatible = device.peripherals[i].meta.compatible;
                if (compatible.indexOf('twim') >= 0) {
                    peripheralDriver = 'TWIM';
                }
                break;
            }
        }

        const sdaPort = sdaPeripheral.peripheralPort.replace('gpio', '');
        const sclPort = sclPeripheral.peripheralPort.replace('gpio', '');

        pinconfigCode = `psels = <NRF_PSEL(${peripheralDriver}_SDA, ${sdaPort}, ${sdaPeripheral.num})>,
                <NRF_PSEL(${peripheralDriver}_SCL, ${sclPort}, ${sclPeripheral.num})>;
        `;
    } else if (device.soc.indexOf('stm32') === 0) {
        const sdaPeripheral = PlatformUtils.getPeripheral(feature.project.device.id, sda, 'gpio');
        const sclPeripheral = PlatformUtils.getPeripheral(feature.project.device.id, scl, 'gpio');

        // pinctrl-0 = <&i2c1_scl_pb8 &i2c1_sda_pb9>;
        const sclPinName = sclPeripheral.peripheralPort.replace('gpio', '');
        const sdaPinName = sdaPeripheral.peripheralPort.replace('gpio', '');

        pinconfigCode = '';
        blocks.push({
            location: 'APP_OVERLAY',
            blockType: BlockType.CODE,
            template: `
&${PORT} {
    status = "okay";
    pinctrl-0 = <&${PORT}_scl_p${sclPinName}${sclPeripheral.num} &${PORT}_sda_p${sdaPinName}${sdaPeripheral.num}>;
    pinctrl-names = "default";
    ${pullCode}
};`,
        });
    } else if (device.soc.indexOf('mim') >= 0) {
        const sdaPinConstant = device.pins.find((pin: any) => pin.num === sda).constant;
        const sclPinConstant = device.pins.find((pin: any) => pin.num === scl).constant;
        let parts = sdaPinConstant.split('_');
        parts.pop();
        parts.pop();
        const sdaPrefix = parts.join('_');
        parts = sclPinConstant.split('_');
        parts.pop();
        parts.pop();
        const sclPrefix = parts.join('_');

        // pinctrl-0 = <&iomuxc_gpio_ad_b1_01_lpi2c1_sda>, <&iomuxc_gpio_ad_b1_00_lpi2c1_scl>;

        pinconfigCode = `
pinmux = <&${sdaPrefix}_${PORT}_sda>, <&${sclPrefix}_${PORT}_scl>;
drive-strength = "r0-6";
drive-open-drain;
slew-rate = "slow";
nxp,speed = "100-mhz";
input-enable;
        `;
    } else if (device.soc.indexOf('samd') === 0) {
        let sdaPin = '';
        let sclPin = '';
        const altFunction = 'D';
        const sdaPeripheral = PlatformUtils.getPeripheral(feature.project.device.id, sda, 'gpio');
        const sclPeripheral = PlatformUtils.getPeripheral(feature.project.device.id, scl, 'gpio');
        if (sdaPeripheral) {
            sdaPin = sdaPeripheral.peripheralPort.replace('port', 'P').toUpperCase();
            sdaPin += sdaPeripheral.num;
        }
        if (sclPeripheral) {
            sclPin = sclPeripheral.peripheralPort.replace('port', 'P').toUpperCase();
            sclPin += sclPeripheral.num;
        }
        const atmelPort = PORT.toUpperCase();

        pinconfigCode = `pinmux = <${sdaPin}${altFunction}_${atmelPort}_PAD0>, <${sclPin}${altFunction}_${atmelPort}_PAD1>;
        `;
    }

    if (pinconfigCode !== '') {
        blocks.push({
            location: 'APP_OVERLAY_PINCTRL',
            blockType: BlockType.CODE,
            template: `
${NAME}_default: ${NAME}_default {
    group1 {
        ${pinconfigCode}

        ${pullCode}
    };
};`,
        });

        blocks.push({
            location: 'APP_OVERLAY',
            blockType: BlockType.CODE,
            template: `
&${PORT} {
    status = "okay";

    pinctrl-0 = <&${NAME}_default>;

};`,
        });
    }

    blocks.push({
        location: 'PRJ_CONF',
        blockType: BlockType.CODE,
        id: 'CONFIG_I2C=y',
        template: 'CONFIG_I2C=y',
    });


    return blocks;
};
